// Renders a weekly summary card the parent can send to a family group chat.
//
// PRIVACY: this image leaves the device. It carries the child's FIRST NAME
// only — never a full name, never an age, never a score, never a comparison
// with any other child. Everything is drawn locally; nothing is uploaded.
// Keep this contract when editing: anything added here ends up in a group chat.

const W = 1080;
const H = 1350;

const C = {
  paper: '#FCF9F2',
  ink: '#1D3557',
  inkSoft: '#5A6B85',
  sun: '#FFD166',
  leaf: '#4ADE80',
  line: '#E3E7D2',
};

// Must match index.css. The app only ever loads 'Baloo 2' and 'Nunito';
// naming any other family here silently renders the card in a system font.
const HEAD = "'Baloo 2', 'Nunito', ui-rounded, sans-serif";
const BODY = "'Nunito', system-ui, sans-serif";

// Canvas draws with whatever is already loaded — it will not fetch a webfont on
// demand and it fails silently, so the card must wait for the faces it names.
async function ensureFonts() {
  if (!document.fonts?.load) return;
  try {
    await Promise.all([
      document.fonts.load(`700 62px ${HEAD}`),
      document.fonts.load(`400 32px ${BODY}`),
      document.fonts.load(`600 26px ${BODY}`),
    ]);
    await document.fonts.ready;
  } catch {
    // A system fallback is ugly but shareable. Never block the parent on this.
  }
}

// Lines `text` needs at the current ctx.font. Used to check a moment fits
// BEFORE drawing any of it, so a sentence never runs into the footer box.
function lineCount(ctx, text, maxW) {
  const words = String(text).split(' ');
  let line = '';
  let n = 1;
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxW && line) {
      n++;
      line = word;
    } else {
      line = test;
    }
  }
  return n;
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// Wraps text and returns the y position after the last line drawn.
function wrap(ctx, text, x, y, maxW, lineH) {
  const words = String(text).split(' ');
  let line = '';
  let cy = y;
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxW && line) {
      ctx.fillText(line, x, cy);
      cy += lineH;
      line = word;
    } else {
      line = test;
    }
  }
  if (line) {
    ctx.fillText(line, x, cy);
    cy += lineH;
  }
  return cy;
}

function star(ctx, cx, cy, r, fill) {
  ctx.beginPath();
  for (let i = 0; i < 10; i++) {
    const rad = i % 2 === 0 ? r : r * 0.45;
    const a = (Math.PI / 5) * i - Math.PI / 2;
    const px = cx + Math.cos(a) * rad;
    const py = cy + Math.sin(a) * rad;
    i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
}

/**
 * @param {object} d
 * @param {string} d.name      first name only
 * @param {string[]} d.moments plain sentences, already localised
 * @param {number} d.stars     stars collected this week
 * @param {string} d.topSkill  localised skill label
 * @param {boolean} d.en
 * @param {HTMLImageElement|null} d.piko optional already-loaded mascot image
 * @returns {Promise<Blob|null>}
 */
export async function renderShareCard(d) {
  await ensureFonts();

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // Background
  ctx.fillStyle = C.paper;
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = C.ink;
  ctx.fillRect(0, 0, W, 300);

  // Mascot, if it loaded. Absence must never break the card.
  if (d.piko) {
    const s = 150;
    ctx.save();
    ctx.beginPath();
    ctx.arc(120 + s / 2, 150, s / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(d.piko, 120, 75, s, s);
    ctx.restore();
  }

  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#FFFFFF';
  ctx.font = `700 62px ${HEAD}`;
  ctx.fillText(d.en ? `${d.name}'s week` : `Minggu ${d.name}`, 300, 148);
  ctx.fillStyle = 'rgba(255,255,255,0.72)';
  ctx.font = `600 30px ${BODY}`;
  ctx.fillText(d.en ? 'Play. Learn. Grow.' : 'Main. Belajar. Berkembang.', 300, 196);

  // Moments
  let y = 400;
  ctx.fillStyle = C.ink;
  ctx.font = `700 40px ${HEAD}`;
  ctx.fillText(d.en ? 'First times this week' : 'Kali pertama minggu ini', 90, y);
  y += 60;

  const boxY = H - 340;
  // Malay sentences run longer than their English pairs, so four of them can
  // overflow into the stats box. Drop whole moments that do not fit rather
  // than letting text collide.
  const momentLimit = boxY - 40;
  const moments = (d.moments || []).slice(0, 4);
  ctx.font = `400 32px ${BODY}`;

  if (moments.length === 0) {
    ctx.fillStyle = C.inkSoft;
    y = wrap(
      ctx,
      d.en ? `${d.name} is just getting started.` : `${d.name} baru bermula.`,
      90, y, W - 180, 46,
    );
  } else {
    for (const m of moments) {
      const needed = lineCount(ctx, m, W - 240) * 46 + 26;
      if (y + needed > momentLimit) break;
      star(ctx, 108, y - 10, 16, C.sun);
      ctx.fillStyle = C.ink;
      ctx.font = `400 32px ${BODY}`;
      y = wrap(ctx, m, 150, y, W - 240, 46) + 26;
    }
  }

  // Footer stats
  ctx.strokeStyle = C.line;
  ctx.lineWidth = 3;
  roundRect(ctx, 90, boxY, W - 180, 180, 40);
  ctx.stroke();

  star(ctx, 190, boxY + 78, 34, C.sun);
  ctx.fillStyle = C.ink;
  ctx.font = `700 56px ${HEAD}`;
  ctx.fillText(String(d.stars ?? 0), 240, boxY + 96);
  ctx.fillStyle = C.inkSoft;
  ctx.font = `600 26px ${BODY}`;
  ctx.fillText(d.en ? 'stars collected' : 'bintang dikumpul', 240, boxY + 132);

  if (d.topSkill) {
    ctx.fillStyle = C.inkSoft;
    ctx.font = `600 26px ${BODY}`;
    ctx.fillText(d.en ? 'most practised' : 'paling kerap dimain', 620, boxY + 66);
    ctx.fillStyle = C.ink;
    ctx.font = `700 40px ${HEAD}`;
    ctx.fillText(d.topSkill, 620, boxY + 118);
  }

  ctx.fillStyle = C.inkSoft;
  ctx.font = `700 28px ${HEAD}`;
  ctx.fillText('PintarPlay', 90, H - 90);
  ctx.fillStyle = C.leaf;
  ctx.beginPath();
  ctx.arc(76, H - 99, 9, 0, Math.PI * 2);
  ctx.fill();

  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
}

export function loadPiko(src = '/assets/characters/piko_celebrate.webp') {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

// Share where supported, download everywhere else. Returns how it was handled.
export async function shareCardBlob(blob, filename, title) {
  if (!blob) return 'failed';
  const file = new File([blob], filename, { type: 'image/png' });
  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title });
      return 'shared';
    } catch (e) {
      if (e?.name === 'AbortError') return 'cancelled';
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
  return 'downloaded';
}
